/** @jsxImportSource @emotion/react */
import { Box, useTheme } from '@mui/material'
import { PageTab } from '@app/components/Guide/ui/PageTab'
import { Section } from '../../../components/Guide/ui/Section'

const HomeProcess = () => {
  const theme = useTheme()
  return (
    <PageTab
      header="The process"
      subheader="The Resilience Explorer® is an evolving platform that grows with your adaptation
              journey. Here's how the process unfolds, and who from our team will support you
              at each stage."
      tabs={[]}
    >
      <Section id="home-process-page">
        <Box display={'flex'} flexDirection="row" gap={32} justifyContent={'space-between'}>
          <Box>
            <h2>Project begins</h2>
            <h4>Welcome and kick-off meeting</h4>
            <p>
              The purpose of the welcome meeting is to introduce you to the Urban Intelligence team
              who will set-up Resilience Explorer for you and support your ongoing use of the
              platform.
            </p>
            <p>
              We will take the time in this meeting to understand your specific priorities and
              measures of success. This is a good opportunity to establish the implementation
              objectives, roles, responsibilities and communication processes.
            </p>
            <p>At the welcome meeting, we will provide access to Resilience Explorer® Home.</p>
          </Box>
          <Box
            component="img"
            src={'/images/illustrations/onboarding-process.png'}
            alt="Rex Process"
            sx={{
              height: '400px',
            }}
          ></Box>
        </Box>
      </Section>
      <Section id="processtab-discovery">
        <h2>01 - Data discovery, collection and validation</h2>
        <h4>Data discovery and collection</h4>
        <p>
          The purpose of the data discovery step is for us to identify and request the required data
          for Resilience Explorer® to meet your specific priorities. This step will include:
        </p>
        <ul>
          <li>A data request memo, including examples on data formats and metadata.</li>
          <li>A virtual data discovery workshop with your team.</li>
          <li>
            Identification and initial processing of local, regional and national datasets that may
            enhance your analysis.
          </li>
        </ul>
        <h4>Data validation</h4>
        <p>
          The purpose of the data validation step is for you to review our data assumptions and
          confirm your approval. The key deliverables are:
        </p>
        <ul>
          <li>Element and Hazard Data Reports.</li>
          <li>A preliminary data gap analysis memo.</li>
        </ul>
      </Section>
      <Section id="processtab-building">
        <h2>02 - Building your Resilience Engine</h2>
        <h4>Data processing</h4>
        <p>
          Following data curation, we will prepare and run spatial risk models using the Resilience
          Explorer® Engine. We will provide you with:
        </p>
        <ul>
          <li>A technical methodology overview</li>
          <li>A vulnerability assumptions report</li>
        </ul>
        <p>We will then integrate the results and supporting reports into Resilience Explorer®.</p>
      </Section>
      <Section id="processtab-access">
        <Box
          display="flex"
          flexDirection="row"
          gap={8}
          justifyContent={'space-between'}
          alignItems="flex-start"
        >
          <Box>
            <h2>03 - Super user access and client customisation</h2>
            <h4>Super-user training</h4>
            <p>
              We want to ensure your super-user group are familiar with the Risk module and the risk
              results. To support this, we offer:
            </p>
            <ul>
              <li>1 hour of online training with your super-user group</li>
              <li>
                1 hour online customisation workshop to explore client customisation options such as
                filterable regions, contextual layers, layer groupings and naming conventions.
              </li>
            </ul>
          </Box>
          <Box
            width={460}
            padding={8}
            flexShrink={0}
            sx={{
              backgroundColor: theme.palette.uintel.light,
              color: theme.palette.white.main,
              borderBottomRightRadius: '32px',
            }}
          >
            <strong>Transition to quarterly updates</strong>
            <p>
              Your subscription includes quarterly updates. If you have new data you&apos;d like to
              include, we will update these data each quarter and talk through the new results with
              you. This data may include new hazard data you have commissioned or new climate
              projections, as they become available.
            </p>
          </Box>
        </Box>
      </Section>
      <Section id="processtab-launch">
        <h2>04 - Organisation launch and training</h2>
        <h4>Go-live</h4>
        <p>
          We want to ensure your organisation gets value from Resilience Explorer® from day one. In
          this step, we will provide access to Resilience Explorer® to the relevant people in your
          organisation and offer:
        </p>
        <ul>
          <li>2 hours of online training, including a demonstration using your data.</li>
        </ul>
      </Section>
      <Section id="processtab-closeout">
        <h2>05 - Implementation close out</h2>
        <h4>Close out meeting</h4>
        <p>
          The purpose of the close out meeting is to elicit feedback from you on the implementation
          process and your early usage of Resilience Explorer®. We will discuss the transition to
          rolling, quarterly updates and present our product roadmap.
        </p>
      </Section>
    </PageTab>
  )
}

export default HomeProcess
